import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { MapPin, Upload, X, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { processImages } from '../utils/imageCompression';
import { INDIAN_STATES, getCitiesForState } from '../utils/indianStatesAndCities';

const ReportForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [location, setLocation] = useState({ lat: 28.6139, lng: 77.2090, address: 'New Delhi, India' });
  const [submitting, setSubmitting] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setCompressing(true);
    try {
      const compressedImages = await processImages(files);
      setPhotos(prev => [...prev, ...compressedImages]);
      toast.success(`${files.length} photo(s) compressed and added`);
    } catch (error) {
      toast.error('Failed to process images');
      console.error('Image processing error:', error);
    } finally {
      setCompressing(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleStateChange = (selectedState) => {
    setState(selectedState);
    setCity('');
    setShowCustomCity(false);
    setCustomCity('');
  };

  const handleCityChange = (selectedCity) => {
    if (selectedCity === 'Other') {
      setShowCustomCity(true);
      setCity('');
    } else {
      setShowCustomCity(false);
      setCity(selectedCity);
      setCustomCity('');
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            address: `${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`
          });
          toast.success(t('msg.locationDetected'));
        },
        (error) => {
          toast.error('Could not get location');
        }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) {
      toast.error('Please select a category');
      return;
    }
    
    setSubmitting(true);
    try {
      const finalCity = showCustomCity ? customCity : city;
      
      const reportData = {
        title,
        description,
        category: category.toLowerCase(),
        location,
        state,
        city: finalCity,
        photos,
        urgency
      };
      
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/reports`, reportData, {
       headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success(t('msg.reportSubmitted'));
      navigate(`/reports/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('msg.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('nav.backHome')}
          </button>
          <h1 className="text-3xl font-bold text-gray-900">{t('reportForm.title')}</h1>
          <p className="text-gray-600 mt-2">{t('reportForm.desc')}</p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="card p-8 space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">{t('reportForm.titleLabel')}</label>
            <Input
              data-testid="title-input"
              placeholder={t('reportForm.titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('reportForm.category')}</label>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger data-testid="category-select">
                <SelectValue placeholder={t('reportForm.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pothole">{t('category.pothole')}</SelectItem>
                <SelectItem value="road" disabled className="opacity-50 cursor-not-allowed">{t('category.road')}</SelectItem>
                <SelectItem value="water" disabled className="opacity-50 cursor-not-allowed">{t('category.water')}</SelectItem>
                <SelectItem value="electricity" disabled className="opacity-50 cursor-not-allowed">{t('category.electricity')}</SelectItem>
                <SelectItem value="garbage" disabled className="opacity-50 cursor-not-allowed">{t('category.garbage')}</SelectItem>
                <SelectItem value="safety" disabled className="opacity-50 cursor-not-allowed">{t('category.safety')}</SelectItem>
                <SelectItem value="general" disabled className="opacity-50 cursor-not-allowed">{t('category.general')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('reportForm.description')}</label>
            <Textarea
              data-testid="description-input"
              placeholder={t('reportForm.descPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('reportForm.urgency')}</label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger data-testid="urgency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t('reportForm.low')}</SelectItem>
                <SelectItem value="normal">{t('reportForm.normal')}</SelectItem>
                <SelectItem value="high">{t('reportForm.high')}</SelectItem>
                <SelectItem value="critical">{t('reportForm.critical')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2"> {t("reportForm.state")}  *</label>
              <Select value={state} onValueChange={handleStateChange} required>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  {INDIAN_STATES.map((stateName) => (
                    <SelectItem key={stateName} value={stateName}>
                      {stateName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2"> {t("reportForm.city")} *</label>
              {!showCustomCity ? (
                <Select 
                  value={city} 
                  onValueChange={handleCityChange} 
                  disabled={!state}
                  required
                >
                  <SelectTrigger data-testid="city-select">
                    <SelectValue placeholder={state ? "Select City" : "Select State First"} />
                  </SelectTrigger>
                  <SelectContent>
                    {state && getCitiesForState(state).map((cityName) => (
                      <SelectItem key={cityName} value={cityName}>
                        {cityName}
                      </SelectItem>
                    ))}
                    {state && <SelectItem value="Other">Other</SelectItem>}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-2">
                  <Input
                    data-testid="custom-city-input"
                    placeholder="Enter your city"
                    value={customCity}
                    onChange={(e) => setCustomCity(e.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCustomCity(false);
                      setCustomCity('');
                    }}
                  >
                    Back
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('reportForm.location')}</label>
            <div className="flex gap-2">
              <Input
                data-testid="location-input"
                value={location.address}
                onChange={(e) => setLocation({...location, address: e.target.value})}
                placeholder={t('reportForm.locationPlaceholder')}
              />
              <Button
                data-testid="detect-location-btn"
                type="button"
                onClick={getCurrentLocation}
                variant="outline"
              >
                <MapPin size={18} />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('reportForm.coordinates')}: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('reportForm.photos')}</label>
            <div className="space-y-4">
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={photo}
                        alt={`Upload ${idx + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        data-testid={`remove-photo-${idx}`}
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className={`image-upload-zone cursor-pointer block ${compressing ? 'opacity-50' : ''}`}>
                <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                <p className="text-gray-600">
                  {compressing ? 'Compressing images...' : t('reportForm.uploadPhotos')}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {compressing ? 'Please wait...' : t('reportForm.photoFormat')}
                </p>
                <input
                  data-testid="photo-upload-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={compressing}
                />
              </label>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              data-testid="submit-report-btn"
              type="submit"
              className="flex-1"
              disabled={submitting}
            >
              {submitting ? t('reportForm.submitting') : t('reportForm.submitReport')}
            </Button>
            <Button
              data-testid="cancel-btn"
              type="button"
              variant="outline"
              onClick={() => navigate('/')}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportForm;